import React from 'react';
import { Box, Button, Grid, Paper, Tooltip, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { DatePickerField } from '~/components/DatePickerField/DatePickerField.tsx';
import { DATE_FORMAT, stringToDayjs } from '~/components/DatePickerField/dateUtils';
import { managerComponentStyles } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/components/styles.ts';
import {
  CLEAR_DATE_FILTER_BUTTON_LABEL,
  DATE_FILTER_TITLE,
  DATE_PLACEHOLDER,
  DOWNLOAD_REPORT_BUTTON_LABEL,
  DOWNLOAD_REPORT_BUTTON_LOADING_LABEL,
  END_DATE_LABEL,
  START_DATE_LABEL,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/constants.ts';

interface IDateFilterPanelProps {
  currentDate: Date;
  startDate: Dayjs | null;
  setStartDate: (date: Dayjs | null) => void;
  endDate: Dayjs | null;
  setEndDate: (date: Dayjs | null) => void;
  onClearDates: () => void;
  onDownloadReport: () => void;
  isDownloadDisabled: boolean;
  isDownloadLoading: boolean;
  downloadTooltip: string;
}

const getNormalizedPickerDate = (value: string | Date | null): Dayjs | null => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    const parsedDate = dayjs(value);
    return parsedDate.isValid() ? parsedDate : null;
  }

  return stringToDayjs(value);
};

export const DateFilter: React.FC<IDateFilterPanelProps> = ({
  currentDate,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  onClearDates,
  onDownloadReport,
  isDownloadDisabled,
  isDownloadLoading,
  downloadTooltip,
}) => {
  const today = dayjs(currentDate);

  const handleStartDateChange = (value: string | Date | null) => {
    if (!value) {
      setStartDate(null);
      return;
    }

    const parsedDate = getNormalizedPickerDate(value);

    if (!parsedDate?.isValid()) {
      return;
    }

    const normalizedStartDate = parsedDate.startOf('day');
    setStartDate(normalizedStartDate);

    if (endDate && normalizedStartDate.isAfter(endDate, 'day')) {
      setEndDate(normalizedStartDate.endOf('day'));
    }
  };

  const handleEndDateChange = (value: string | Date | null) => {
    if (!value) {
      setEndDate(null);
      return;
    }

    const parsedDate = getNormalizedPickerDate(value);

    if (!parsedDate?.isValid()) {
      return;
    }

    const normalizedEndDate = parsedDate.endOf('day');
    setEndDate(normalizedEndDate);

    if (startDate && normalizedEndDate.isBefore(startDate, 'day')) {
      setStartDate(normalizedEndDate.startOf('day'));
    }
  };

  return (
    <Paper elevation={0} variant="outlined" sx={managerComponentStyles.filterPaper}>
      <Box sx={managerComponentStyles.filterIntro}>
        <Typography variant="subtitle1" sx={managerComponentStyles.filterTitle}>
          {DATE_FILTER_TITLE}
        </Typography>
      </Box>

      <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ alignItems: 'flex-start' }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Box sx={managerComponentStyles.datePickerField}>
            <DatePickerField
              label={START_DATE_LABEL}
              placeholder={DATE_PLACEHOLDER}
              date={startDate?.format(DATE_FORMAT) ?? null}
              onDateChange={handleStartDateChange}
              maxDate={(endDate ?? today).toDate()}
              dataTestId="covenants-start-date-filter"
            />
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Box sx={managerComponentStyles.datePickerField}>
            <DatePickerField
              label={END_DATE_LABEL}
              placeholder={DATE_PLACEHOLDER}
              date={endDate?.format(DATE_FORMAT) ?? null}
              onDateChange={handleEndDateChange}
              minDate={startDate?.toDate() ?? null}
              maxDate={today.toDate()}
              dataTestId="covenants-end-date-filter"
            />
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Box sx={managerComponentStyles.dateSpacer}>
            <Button
              variant="outlined"
              onClick={onClearDates}
              disabled={!startDate && !endDate}
              sx={managerComponentStyles.dateClearBtn}
            >
              {CLEAR_DATE_FILTER_BUTTON_LABEL}
            </Button>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Box sx={managerComponentStyles.dateReportSpacer}>
            <Tooltip title={downloadTooltip} arrow disableHoverListener={!isDownloadDisabled}>
              <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
                <Button
                  variant="contained"
                  onClick={onDownloadReport}
                  disabled={isDownloadDisabled || isDownloadLoading}
                  sx={managerComponentStyles.dateReportBtn}
                >
                  {isDownloadLoading
                    ? DOWNLOAD_REPORT_BUTTON_LOADING_LABEL
                    : DOWNLOAD_REPORT_BUTTON_LABEL}
                </Button>
              </Box>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

